/**
 * Pricing Event Tags
 *
 * These are granular tags from the pricing_event_tag enum in the database.
 * They provide more detailed categorization than the broader event categories.
 */

export type PricingEventTag =
  // Pricing Changes (9)
  | 'price_increase'
  | 'price_decrease'
  | 'pricing_restructure'
  | 'usage_based_pricing'
  | 'seat_based_pricing'
  | 'currency_region'
  | 'billing_change'
  | 'contract_terms'
  // Plan Changes (5)
  | 'new_plan'
  | 'plan_removed'
  | 'plan_renamed'
  | 'plan_consolidated'
  | 'new_addon'
  // Discount/Trial Changes (6)
  | 'discount_added'
  | 'discount_removed'
  | 'annual_discount_change'
  | 'volume_discount'
  | 'trial_change'
  | 'freemium_change'
  // Free Tier Changes (2)
  | 'free_tier_added'
  | 'free_tier_removed'
  // Feature Changes (8)
  | 'feature_added'
  | 'feature_removed'
  | 'premium_tier_movement'
  | 'feature_democratized'
  | 'feature_unbundled'
  | 'ai_ml_features'
  | 'automation_features'
  | 'copilot_assistant'
  // Enterprise/Security (4)
  | 'enterprise_features'
  | 'security_compliance'
  | 'sso_saml'
  | 'admin_controls'
  // Integration/API (4)
  | 'api_changes'
  | 'integration_added'
  | 'integration_removed'
  | 'developer_tools'
  // Capacity/Limits (8)
  | 'capacity_increase'
  | 'capacity_decrease'
  | 'unlimited_added'
  | 'unlimited_removed'
  | 'storage_change'
  | 'api_rate_change'
  | 'user_limit_change'
  | 'usage_limit_change'
  // Other (7)
  | 'page_redesign'
  | 'branding_change'
  | 'clarification'
  | 'deprecation_notice'
  | 'grandfathering';

/**
 * Array of all pricing event tags (44 total)
 */
export const ALL_PRICING_EVENT_TAGS: PricingEventTag[] = [
  // Pricing Changes (9)
  'price_increase',
  'price_decrease',
  'pricing_restructure',
  'usage_based_pricing',
  'seat_based_pricing',
  'currency_region',
  'billing_change',
  'contract_terms',
  // Plan Changes (5)
  'new_plan',
  'plan_removed',
  'plan_renamed',
  'plan_consolidated',
  'new_addon',
  // Discount/Trial Changes (6)
  'discount_added',
  'discount_removed',
  'annual_discount_change',
  'volume_discount',
  'trial_change',
  'freemium_change',
  // Free Tier Changes (2)
  'free_tier_added',
  'free_tier_removed',
  // Feature Changes (8)
  'feature_added',
  'feature_removed',
  'premium_tier_movement',
  'feature_democratized',
  'feature_unbundled',
  'ai_ml_features',
  'automation_features',
  'copilot_assistant',
  // Enterprise/Security (4)
  'enterprise_features',
  'security_compliance',
  'sso_saml',
  'admin_controls',
  // Integration/API (4)
  'api_changes',
  'integration_added',
  'integration_removed',
  'developer_tools',
  // Capacity/Limits (8)
  'capacity_increase',
  'capacity_decrease',
  'unlimited_added',
  'unlimited_removed',
  'storage_change',
  'api_rate_change',
  'user_limit_change',
  'usage_limit_change',
  // Other (7)
  'page_redesign',
  'branding_change',
  'clarification',
  'deprecation_notice',
  'grandfathering',
];

/**
 * Format a tag for display (converts snake_case to Title Case)
 */
export const formatTagLabel = (tag: PricingEventTag | string): string => {
  return tag
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get color styling for a tag based on its semantic meaning
 */
export const getTagColor = (tag: PricingEventTag | string): string => {
  const tagLower = tag.toLowerCase();

  // Positive changes (green)
  if (
    tagLower.includes('increase') ||
    tagLower.includes('added') ||
    tagLower.includes('new_') ||
    tagLower.includes('democratized')
  ) {
    return 'bg-green-50 text-green-700 border-green-200';
  }

  // Negative changes (red)
  if (
    tagLower.includes('decrease') ||
    tagLower.includes('removed') ||
    tagLower.includes('deprecation')
  ) {
    return 'bg-red-50 text-red-700 border-red-200';
  }

  // Structural changes (blue)
  if (
    tagLower.includes('restructure') ||
    tagLower.includes('change') ||
    tagLower.includes('renamed') ||
    tagLower.includes('consolidated')
  ) {
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }

  // Features/capabilities (purple)
  if (
    tagLower.includes('feature') ||
    tagLower.includes('ai_ml') ||
    tagLower.includes('automation') ||
    tagLower.includes('copilot')
  ) {
    return 'bg-purple-50 text-purple-700 border-purple-200';
  }

  // Enterprise/security (indigo)
  if (
    tagLower.includes('enterprise') ||
    tagLower.includes('security') ||
    tagLower.includes('sso') ||
    tagLower.includes('admin')
  ) {
    return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  }

  // Default (gray)
  return 'bg-gray-50 text-gray-700 border-gray-200';
};
